import { useState } from 'react';

const countries = [
  ['EC', 'Ecuador'],
  ['AR', 'Argentina'],
  ['BO', 'Bolivia'],
  ['BR', 'Brasil'],
  ['CL', 'Chile'],
  ['CO', 'Colombia'],
  ['CR', 'Costa Rica'],
  ['CU', 'Cuba'],
  ['DO', 'República Dominicana'],
  ['SV', 'El Salvador'],
  ['ES', 'España'],
  ['GT', 'Guatemala'],
  ['HN', 'Honduras'],
  ['MX', 'México'],
  ['NI', 'Nicaragua'],
  ['PA', 'Panamá'],
  ['PY', 'Paraguay'],
  ['PE', 'Perú'],
  ['PR', 'Puerto Rico'],
  ['US', 'Estados Unidos'],
  ['UY', 'Uruguay'],
  ['VE', 'Venezuela'],
] as const;

type FormState = {
  accountType: 'listener' | 'artist';
  publicName: string;
  artisticName: string;
  legalName: string;
  countryCode: string;
  acceptTerms: boolean;
};

const initialForm: FormState = {
  accountType: 'listener',
  publicName: '',
  artisticName: '',
  legalName: '',
  countryCode: 'EC',
  acceptTerms: false,
};

export function ProfilePanel({ onCompleted }: { onCompleted: () => Promise<void> }) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    const response = await fetch('/api/v1/me/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...form, artisticName: form.artisticName || null }),
    });
    const body = (await response.json()) as { detail?: string };
    if (response.ok) {
      window.dispatchEvent(new Event('agam:profile-updated'));
      await onCompleted();
    } else setMessage(body.detail ?? 'No fue posible guardar el perfil.');
    setSaving(false);
  }

  return (
    <section id="perfil" className="glass-panel rounded-[2rem] p-6 sm:p-8">
      <p className="eyebrow">Identidad inicial</p>
      <h2 className="mt-2 text-2xl font-semibold text-white">Completa tu perfil</h2>
      <p className="mt-2 text-sm leading-6 text-zinc-500">
        Elige tus datos con cuidado: quedarán vinculados a tu historial competitivo y no podrán
        modificarse desde la cuenta.
      </p>
      <form onSubmit={save} className="mt-7 grid gap-5 sm:grid-cols-2">
        <label className="field-label sm:col-span-2">
          ¿Cómo participarás?
          <span className="mt-2 grid grid-cols-2 gap-2">
            {(['listener', 'artist'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => update('accountType', type)}
                className={`rounded-xl border px-4 py-3 text-sm transition ${form.accountType === type ? 'border-fuchsia-400/70 bg-fuchsia-400/10 text-white' : 'border-white/10 text-zinc-500'}`}
              >
                {type === 'artist' ? 'Soy artista' : 'Soy oyente'}
              </button>
            ))}
          </span>
        </label>
        <Field label="Alias público">
          <input
            className="field-input"
            value={form.publicName}
            onChange={(e) => update('publicName', e.target.value)}
            required
            minLength={3}
            maxLength={40}
          />
        </Field>
        {form.accountType === 'artist' && (
          <Field label="Nombre artístico">
            <input
              className="field-input"
              value={form.artisticName}
              onChange={(e) => update('artisticName', e.target.value)}
              required
              minLength={3}
              maxLength={60}
            />
          </Field>
        )}
        <Field label="Nombre real" privateField>
          <input
            className="field-input"
            value={form.legalName}
            onChange={(e) => update('legalName', e.target.value)}
            required
            maxLength={100}
            autoComplete="name"
          />
        </Field>
        <Field label="País">
          <select
            className="field-input"
            value={form.countryCode}
            onChange={(e) => update('countryCode', e.target.value)}
          >
            {countries.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <div className="rounded-xl border border-emerald-400/15 bg-emerald-400/[0.04] p-4 text-xs leading-5 text-emerald-100/75">
          Tu nombre real se cifra y nunca aparece en rankings, votos ni comentarios.
        </div>
        <label className="flex items-start gap-3 text-xs leading-5 text-zinc-400 sm:col-span-2">
          <input
            type="checkbox"
            checked={form.acceptTerms}
            onChange={(e) => update('acceptTerms', e.target.checked)}
            required
            className="mt-1 accent-fuchsia-400"
          />
          Confirmo que mis datos son correctos y acepto las reglas de convivencia, privacidad y
          participación de AGAM.
        </label>
        {message && (
          <p role="status" className="text-sm text-fuchsia-200 sm:col-span-2">
            {message}
          </p>
        )}
        <button
          disabled={saving}
          className="rounded-full bg-white px-6 py-3 text-sm font-bold text-zinc-950 hover:bg-fuchsia-200 disabled:opacity-50 sm:col-span-2"
        >
          {saving ? 'Guardando…' : 'Entrar a la arena'}
        </button>
      </form>
    </section>
  );
}

function Field({
  label,
  privateField,
  children,
}: {
  label: string;
  privateField?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="field-label">
      <span className="flex items-center justify-between gap-3">
        {label}
        {privateField && (
          <span className="text-[0.6rem] uppercase tracking-widest text-emerald-300">Privado</span>
        )}
      </span>
      <span className="mt-2 block">{children}</span>
    </label>
  );
}
